import { createDomain, sample } from 'effector'
import { Car, OsagoImageImportResult } from '~/shared/api'
import { snackbarEnqueued } from '~/shared/lib/notifications'

const POLL_INTERVAL_MS = 3000
const POLL_TIMEOUT_MS = 3600_000

const domain = createDomain('pages.cars.list.importOsagoImageTemplate')

export type ImportStatus =
  | 'idle'
  | 'uploading'
  | 'pending'
  | 'processing'
  | 'done'
  | 'failed'

const pollStatusUpdated = domain.createEvent<'pending' | 'processing'>()

export const $importStatus = domain.createStore<ImportStatus>('idle')

async function pollStatus(jobId: string): Promise<OsagoImageImportResult> {
  const deadline = Date.now() + POLL_TIMEOUT_MS

  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))

    const status = await Car.osagoImageMassImportStatus(jobId)

    if (status.status === 'done') {
      return {
        processed: status.processed,
        skipped: status.skipped,
        errors: status.errors,
      }
    }

    if (status.status === 'failed') {
      throw new Error(status.message)
    }

    pollStatusUpdated(status.status)
  }

  throw new Error('Превышено время ожидания импорта')
}

export const osagoImageMassImportFx = domain.createEffect<
  File,
  OsagoImageImportResult,
  Error
>({
  async handler(file) {
    const { jobId } = await Car.osagoImageMassImport(file)
    return await pollStatus(jobId)
  },
})

$importStatus
  .on(osagoImageMassImportFx, () => 'uploading')
  .on(pollStatusUpdated, (_, status) => status)
  .on(osagoImageMassImportFx.done, () => 'done')
  .on(osagoImageMassImportFx.fail, () => 'failed')

sample({
  clock: osagoImageMassImportFx.failData,
  fn(e) {
    return {
      message: e.message,
      variant: 'error' as const,
    }
  },
  target: [snackbarEnqueued],
})
